import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { IMAGES } from '@/../images';
import { InitialPage, SuccessMessage, ProgressBar, SupportFormField, StreakRecoveryPage } from '@/components/support';
import { BRAND_CONFIG, SUPPORT_CATEGORIES, LEGACY_NEWSLETTER_MAP, type BrandKey } from '@/types/brands';
import { formSchema, type FormValues, type Question, type GroupField, type SingleField } from '@/types/supportTypes';
import { SolutionPage } from "@/components/support/SolutionPage";
import { resolutionService } from "@/lib/support-docs/resolution-service";
import { SupportTicket, AIResolutionAttempt, SupportSolution } from "@/types/support";
import { Loader2, Bot } from 'lucide-react';

function resolveBrandFromParam(param: string | null): string {
  if (!param) return '';
  if (param in BRAND_CONFIG) return param;
  if (param in LEGACY_NEWSLETTER_MAP) return LEGACY_NEWSLETTER_MAP[param];
  return '';
}

export function SupportForm() {
  const [searchParams] = useSearchParams();
  const initialBrand = resolveBrandFromParam(searchParams.get('newsletter'));

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showInitialPage, setShowInitialPage] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  // Estados para o sistema de resolução inteligente
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showSolutionPage, setShowSolutionPage] = useState(false);
  const [currentAttempt, setCurrentAttempt] = useState<AIResolutionAttempt | null>(null);
  const [currentSolution, setCurrentSolution] = useState<SupportSolution | null>(null);
  const [needMoreInfo, setNeedMoreInfo] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [showFinalTicketPage, setShowFinalTicketPage] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [showStreakRecovery, setShowStreakRecovery] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      newsletter: initialBrand,
      main: "",
      issue: "",
      description: "",
      email: searchParams.get('email') || "",
    },
    mode: "onSubmit"
  });

  const selectedBrand = form.watch('newsletter') as BrandKey;
  const brandConfig = selectedBrand ? BRAND_CONFIG[selectedBrand] : null;
  const hasFullCategories = brandConfig?.hasFullCategories ?? false;

  // Config compatível com SolutionPage
  const config = {
    color: brandConfig?.colorFrom || '#ffce04',
    logo: IMAGES.newLogos.theNews,
    title: brandConfig?.newName || 'suporte grupo TNS',
  };

  const getFilteredCategories = () => {
    return Object.entries(SUPPORT_CATEGORIES).filter(([category]) => {
      if (category.includes('Portal')) return selectedBrand === 'thenews';
      if (hasFullCategories) return true;
      return !category.includes('Streak') &&
        !category.includes('prêmio') &&
        !category.includes('indicação');
    }).reduce((acc, [category, issues]) => {
      acc[category] = issues;
      return acc;
    }, {} as Record<string, readonly string[]>);
  };

  const filteredCategories = getFilteredCategories();

  const formQuestions: Question[] = [
    {
      id: 'category_group',
      label: 'Selecione a categoria e o problema',
      isGroup: true,
      fields: [
        { id: 'newsletter', label: 'Marca', type: 'radio', value: '', onChange: () => { } },
        { id: 'main', label: 'Categoria Principal', type: 'radio', value: '', onChange: () => { } },
        { id: 'issue', label: 'Problema Específico', type: 'radio', value: '', onChange: () => { } }
      ]
    },
    {
      id: 'description',
      label: 'Conte-nos melhor sobre o seu problema',
      placeholder: 'Por favor, descreva o que está acontecendo...',
      isGroup: false,
      type: 'textarea',
      value: '',
      onChange: () => { }
    },
    {
      id: 'email',
      label: 'Qual seu e-mail para contato?',
      placeholder: 'seu@email.com',
      isGroup: false,
      type: 'email',
      value: '',
      onChange: () => { }
    }
  ];

  const startForm = () => {
    setShowInitialPage(false);
    setCurrentQuestionIndex(0);
  };

  useEffect(() => {
    if (currentQuestionIndex === -1) {
      setProgress(0);
    } else {
      setProgress(((currentQuestionIndex + 1) / formQuestions.length) * 100);
    }
  }, [currentQuestionIndex, formQuestions.length]);

  const analyzeAndOfferSolution = async (data: FormValues): Promise<boolean> => {
    setIsAnalyzing(true);

    try {
      const ticket: SupportTicket = {
        main: data.main,
        issue: data.issue,
        description: data.description,
        email: data.email,
        newsletter: data.newsletter,
        wantReply: true
      };

      const attempt = await resolutionService.analyzeTicket(ticket);
      setCurrentAttempt(attempt);

      if (resolutionService.shouldOfferAutomatedSolution(attempt)) {
        const solution = resolutionService.findSolutionForProblem(attempt.problemMatched!);
        if (solution) {
          setCurrentSolution(solution);
          setShowSolutionPage(true);
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error('Erro ao analisar ticket:', error);
      return false;
    } finally {
      setIsAnalyzing(false);
    }
  };

  const onSubmit = async (data: FormValues) => {
    setShowFinalTicketPage(true);
    await sendTicketToSupportWithFinalPage(data);
  };

  const handleCreateTicketAfterSolution = () => {
    setShowSolutionPage(false);
    if (!form.getValues('email')) {
      setCurrentQuestionIndex(2);
    } else {
      setNeedMoreInfo(true);
    }
  };

  const handleSubmitAdditionalInfo = () => {
    const data = form.getValues();
    const finalData = {
      ...data,
      description: data.description + '\n\n--- Informações adicionais ---\n' + additionalInfo
    };
    setNeedMoreInfo(false);
    setShowFinalTicketPage(true);
    sendTicketToSupportWithFinalPage(finalData);
  };

  const sendTicketToSupportWithFinalPage = async (data: FormValues) => {
    try {
      setLoading(true);
      setError('');

      const ticketPayload = {
        main: data.main,
        issue: data.issue,
        description: data.description,
        email: data.email,
        newsletter: data.newsletter,
        wantReply: true,
        aiAttempted: currentAttempt ? true : false,
        aiSolutionOffered: currentSolution ? true : false,
        aiSolutionAccepted: false
      };

      let response: Response;

      if (attachedFiles.length > 0) {
        const multipartData = new FormData();
        Object.entries(ticketPayload).forEach(([key, value]) => {
          multipartData.append(key, String(value));
        });
        attachedFiles.forEach((file) => {
          multipartData.append('arquivos', file);
        });
        response = await fetch('https://worker.thenewscc.com/support', {
          method: 'POST',
          body: multipartData,
        });
      } else {
        response = await fetch('https://worker.thenewscc.com/support', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(ticketPayload),
        });
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Erro ao enviar formulário' }));
        throw new Error(errorData.message || 'Erro ao enviar formulário');
      }

      setIsSuccess(true);

      setTimeout(() => {
        setShowFinalTicketPage(false);
        setIsSuccess(false);
        setShowInitialPage(true);
        setCurrentQuestionIndex(-1);
        setCurrentAttempt(null);
        setCurrentSolution(null);
        setNeedMoreInfo(false);
        setAdditionalInfo('');
        setAttachedFiles([]);
        form.reset();
      }, 5000);

    } catch (error) {
      console.error('Erro:', error);
      setError(error instanceof Error ? error.message : 'Ocorreu um erro ao enviar o formulário. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackFromSolution = () => {
    setShowSolutionPage(false);
    setCurrentQuestionIndex(1);
    setCurrentAttempt(null);
    setCurrentSolution(null);
  };

  const handleChangeSolution = (solutionId: string) => {
    const newSolution = resolutionService.solutions.find(s => s.id === solutionId);
    if (newSolution) {
      setCurrentSolution(newSolution);
      const newAttempt: AIResolutionAttempt = {
        ticketId: currentAttempt?.ticketId || 'temp-' + Date.now(),
        problemMatched: newSolution.problemId,
        solutionOffered: newSolution.id,
        confidenceScore: 0.8,
        timestamp: new Date()
      };
      setCurrentAttempt(newAttempt);
    }
  };

  const handleNextQuestion = async () => {
    const currentQuestion = formQuestions[currentQuestionIndex];
    let isValid = true;

    if (currentQuestion.isGroup) {
      const mainValue = form.getValues('main');
      const issueValue = form.getValues('issue');

      if (!mainValue) {
        isValid = false;
        form.setError('main', { message: 'Por favor, selecione uma categoria' });
      }
      if (!issueValue && mainValue && !['Feedback e Erros', 'Outros', 'Quiz'].includes(mainValue)) {
        isValid = false;
        form.setError('issue', { message: 'Por favor, selecione um problema específico' });
      }

      if (isValid && mainValue) {
        if (['Feedback e Erros', 'Outros', 'Quiz'].includes(mainValue)) {
          window.location.href = 'https://tally.so/r/w2o7GL';
          return;
        }
        if (issueValue === 'Outro' && selectedBrand === 'thenews') {
          window.location.href = 'https://tally.so/r/w2o7GL';
          return;
        }
        if (mainValue === 'Streak (pontuação diária)') {
          setShowStreakRecovery(true);
          return;
        }
      }
    } else {
      const fieldName = currentQuestion.id as keyof FormValues;
      const value = form.getValues(fieldName);
      if (!value && !currentQuestion.optional) {
        isValid = false;
        form.setError(fieldName, { message: 'Campo obrigatório' });
      }

      if (currentQuestion.id === 'description') {
        if (value && value.length < 10) {
          isValid = false;
          form.setError('description', { message: 'A descrição deve ter pelo menos 10 caracteres' });
        } else if (isValid && value) {
          const mainValue = form.getValues('main');
          const issueValue = form.getValues('issue');
          const tempData = {
            main: mainValue,
            issue: issueValue,
            description: value,
            email: '',
            newsletter: form.getValues('newsletter')
          };
          const solutionOffered = await analyzeAndOfferSolution(tempData as FormValues);
          if (solutionOffered) return;
        }
      }

      if (currentQuestion.id === 'email' && value && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
        isValid = false;
        form.setError('email', { message: 'E-mail inválido' });
      }
    }

    if (isValid && currentQuestionIndex < formQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const getCurrentQuestion = () => formQuestions[currentQuestionIndex];

  if (loading || isAnalyzing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <motion.img
            src={IMAGES.newLogos.theNews}
            alt="Carregando..."
            className="w-32 h-32 object-contain mx-auto mb-4"
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
          <p className="text-lg text-gray-600">
            {isAnalyzing ? (
              <>
                <Bot className="inline-block w-5 h-5 mr-2" />
                Analisando seu problema...
              </>
            ) : (
              'Enviando para o suporte...'
            )}
          </p>
        </div>
      </div>
    );
  }

  if (showStreakRecovery) {
    return (
      <StreakRecoveryPage
        onBack={() => {
          setShowStreakRecovery(false);
          setCurrentQuestionIndex(0);
        }}
        onOpenTicket={() => {
          setShowStreakRecovery(false);
          setCurrentQuestionIndex(1);
        }}
      />
    );
  }

  if (showSolutionPage) {
    return (
      <SolutionPage
        solution={currentSolution}
        attempt={currentAttempt}
        ticket={form.getValues()}
        newsletterColor={config.color}
        onBack={handleBackFromSolution}
        onCreateTicket={handleCreateTicketAfterSolution}
        config={config}
        onChangeSolution={handleChangeSolution}
      />
    );
  }

  if (needMoreInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-8">
        <div className="w-full max-w-3xl p-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-gray-100"
          >
            <img
              src={IMAGES.newLogos.theNews}
              alt={config.title}
              className="w-32 mx-auto mb-6"
            />
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 text-center">
              Entendemos que a solução não resolveu seu problema
            </h2>
            <p className="text-gray-600 text-center mb-8">
              Por favor, conte-nos mais detalhes para que possamos ajudar melhor
            </p>

            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                Informações adicionais sobre o problema:
              </label>
              <textarea
                value={additionalInfo}
                onChange={(e) => setAdditionalInfo(e.target.value)}
                placeholder="Descreva em mais detalhes o que está acontecendo, o que você já tentou fazer, quando o problema começou, etc."
                className="w-full min-h-[200px] p-4 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 resize-none focus:border-[#ffce04] focus:ring-2 focus:ring-[#ffce04]/20 transition-all shadow-sm hover:border-gray-300"
              />
              <div className="text-sm text-gray-500 text-right">
                {additionalInfo.length} caracteres
              </div>
            </div>

            <div className="mt-8 flex justify-between items-center">
              <button
                onClick={() => {
                  setNeedMoreInfo(false);
                  setShowSolutionPage(true);
                }}
                className="text-base md:text-lg text-gray-500 hover:text-gray-900 transition-colors"
              >
                ← voltar
              </button>
              <Button
                onClick={handleSubmitAdditionalInfo}
                disabled={additionalInfo.length < 10}
                className="bg-gradient-to-r from-[#ffce04] to-[#ff8a47] hover:opacity-90 text-white px-8 py-6 text-lg rounded-xl transition-opacity disabled:opacity-50"
              >
                Enviar ticket para suporte
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (showFinalTicketPage) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-8">
        <div className="w-full max-w-3xl p-3 md:p-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl p-4 md:p-12 shadow-sm border border-gray-100 text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-[#ffce04] to-[#ff8a47] text-white mx-auto mb-6"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </motion.div>

            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Ticket de Suporte Criado!
            </h2>

            <div className="bg-gray-50 rounded-xl p-3 md:p-6 mb-8 text-left">
              <h3 className="font-semibold text-gray-800 mb-3">Detalhes do seu ticket:</h3>
              <div className="space-y-2 text-sm">
                <p><span className="font-medium">Categoria:</span> {form.getValues('main')}</p>
                <p><span className="font-medium">Problema:</span> {form.getValues('issue')}</p>
                <p><span className="font-medium">E-mail:</span> {form.getValues('email')}</p>
                <p><span className="font-medium">Status:</span> <span className="text-green-600 font-medium">Em análise</span></p>
              </div>
            </div>

            <p className="text-xl text-gray-700 mb-4">
              Recebemos seu ticket e nossa equipe está analisando
            </p>
            <p className="text-gray-600 mb-8">
              Você receberá uma resposta em até 24 horas úteis no e-mail informado
            </p>

            {loading ? (
              <div className="flex justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-[#ffce04]" />
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                Redirecionando em alguns segundos...
              </p>
            )}
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center pt-8 relative">
      <div className="w-full max-w-5xl p-3">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex-1 flex flex-col justify-center text-center"
        >
          <img
            src={IMAGES.newLogos.theNews}
            alt="Suporte Grupo TNS"
            className="w-48 mx-auto mb-8"
          />

          <AnimatePresence mode="wait">
            {showInitialPage ? (
              <InitialPage onStart={startForm} />
            ) : isSuccess ? (
              <SuccessMessage />
            ) : (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <motion.div
                    key={currentQuestionIndex}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white rounded-3xl py-8 px-4 md:p-12 shadow-sm border border-gray-100 max-w-3xl mx-auto"
                  >
                    {getCurrentQuestion().isGroup ? (
                      <div className="space-y-6">
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                          {getCurrentQuestion().label}
                        </h2>
                        <div className="space-y-6">
                          {(getCurrentQuestion() as GroupField).fields.map((field) => (
                            <FormField
                              key={field.id}
                              control={form.control}
                              name={field.id as keyof FormValues}
                              render={({ field: fieldProps }) => (
                                <FormItem>
                                  <FormControl>
                                    <SupportFormField
                                      field={{ ...field, ...fieldProps }}
                                      form={form}
                                      filteredCategories={filteredCategories}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                          {getCurrentQuestion().label}
                        </h2>
                        <FormField
                          control={form.control}
                          name={getCurrentQuestion().id as keyof FormValues}
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <SupportFormField
                                  field={{ ...getCurrentQuestion() as SingleField, ...field }}
                                  form={form}
                                  filteredCategories={filteredCategories}
                                  attachedFiles={attachedFiles}
                                  onAttachFiles={setAttachedFiles}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    )}

                    <div className="mt-8 flex justify-between items-center">
                      <button
                        type="button"
                        onClick={handlePreviousQuestion}
                        disabled={currentQuestionIndex === 0}
                        className="text-base md:text-lg text-gray-500 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        ← anterior
                      </button>
                      {currentQuestionIndex === formQuestions.length - 1 ? (
                        <Button
                          type="submit"
                          className="bg-gradient-to-r from-[#ffce04] to-[#ff8a47] hover:opacity-90 text-white px-8 py-6 text-lg rounded-xl transition-opacity"
                          disabled={loading}
                        >
                          {loading ? (
                            <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            'enviar'
                          )}
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          onClick={handleNextQuestion}
                          className="bg-gradient-to-r from-[#ffce04] to-[#ff8a47] hover:opacity-90 text-white px-8 py-6 text-lg rounded-xl transition-opacity"
                        >
                          próximo →
                        </Button>
                      )}
                    </div>
                  </motion.div>

                  <ProgressBar
                    progress={progress}
                    currentIndex={currentQuestionIndex}
                    total={formQuestions.length}
                  />

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-gray-50 rounded-xl p-4 border border-gray-200 text-gray-700 text-center max-w-2xl mx-auto"
                  >
                    <p className="text-sm">
                      {selectedBrand === 'thenews' ? (
                        "Se você quer comentar sobre a edição ou sinalizar algum erro, use o espaço logo após a pergunta 'Na sua visão, a edição de hoje foi imparcial?'. É lá que você pode escrever sugestões ou recados pro nosso time de redação!"
                      ) : (
                        "Quer deixar um comentário sobre a edição ou apontar algum erro? Use o espaço no final da edição, na seção de opinião dos leitores. É lá que você pode enviar sugestões, recados ou observações para o nosso time de redação."
                      )}
                    </p>
                  </motion.div>

                  {error && (
                    <motion.p
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-red-500 text-center mt-2"
                    >
                      {error}
                    </motion.p>
                  )}
                </form>
              </Form>
            )}
          </AnimatePresence>
        </motion.div>

        <div className="absolute bottom-0 left-0 w-full h-2 bg-gray-200">
          <motion.div
            className="h-full bg-gradient-to-r from-[#ffce04] to-[#ff8a47]"
            initial={{ width: "0%" }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>
    </div>
  );
}
